// M01 Anmelden (+ Hinweis zur Ersteinrichtung).
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { qk, useSetupStatus } from '../api/queries';
import type { Me } from '../api/types';
import { Field } from '../components/ui';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setup = useSetupStatus();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ mfaRequired: boolean; me: Me }>('/auth/login', { email, password });
      // M02: bei res.mfaRequired zeigt die App den zweiten Schritt (me.mfaPending)
      // "Angemeldet bleiben" (M01) betrifft nur die Cookie-Persistenz; der Server setzt sie heute immer.
      void remember;
      qc.setQueryData(qk.me, res.me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setError(t('login.locked'));
      else if (err instanceof ApiError && (err.status === 401 || err.status === 422)) setError(t('login.invalid'));
      else setError(t('errors.network'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1>{t('login.title')}</h1>
      <div className="muted">{t('login.sub')}</div>
      {setup.data?.needsSetup && (
        <div className="hint warn" style={{ marginTop: 18 }} role="note">
          <b>{t('login.setupTitle')}</b>
          <div style={{ marginTop: 4 }}>{t('login.setupText')}</div>
          <code className="mono" style={{ display: 'block', marginTop: 6, overflowWrap: 'anywhere' }}>pnpm --filter @disploraq/core create-admin -- --email admin@firma.ch --password '…'</code>
        </div>
      )}
      <form onSubmit={submit} noValidate>
        <Field label={t('login.email')}>
          <input type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.ch" />
        </Field>
        <Field label={t('login.password')}>
          <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </Field>
        <div className="row">
          <label className="check"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />{t('login.remember')}</label>
          <span className="grow" />
          <Link className="small" to="/password/forgot">{t('login.forgot')}</Link>
        </div>
        <div className="error" role="alert">{error}</div>
        <button className="btn primary" disabled={busy || !email || !password}>{busy ? t('common.loading') : t('login.submit')}</button>
      </form>
    </AuthLayout>
  );
}
